// client/src/features/portal/scope.ts — „which contract am I looking at" · PORTAL-002 phase 2.
//
// A tenant is a party to one contract almost always and to several sometimes — a family that
// rents a flat and a parking space from the same landlord, or one that moved between two of
// the landlord's flats. 04 §6.6 puts a „property/contract selector (when the tenant has
// several)" in the portal header for exactly that case, and D-1173 left the slot marked
// because PORTAL-001 had not built the endpoint that can count. It has, so this is the
// resolution the selector and three of the five screens share.
//
// ── the rule, in one place ─────────────────────────────────────────────────────────────────
// The URL names a contract; if it names one of mine, that is the one. Otherwise the DEFAULT is
// the first ACTIVE contract in the server's own order (`start_date` descending — the most
// recent lease first), and failing that the server's first row whatever its status, so a tenant
// whose lease has ended still opens on something rather than on an empty state.
//
// AN ID THAT IS NOT MINE FALLS BACK TO THE DEFAULT rather than rendering an error. The list is
// already narrowed to the caller's own contracts by `PortalScopeResolver`, so an id outside it
// is a stale link, a shared URL from another tenant, or a typo — none of which is a fault the
// tenant can fix and none of which should replace „Chiria mea" with a problem report. The API
// answers the same shape of question with a 404 and nothing here weakens that: every request
// this client makes still carries the resolved id, and a request for someone else's contract
// is never made because the id never becomes the one in view.

import { useSearch } from '@tanstack/react-router';

import type { PortalContractListItem } from '@/client';

/**
 * The `?contract=` of the URL, read from wherever under `/portal` the caller renders.
 *
 * `strict: false` because the callers are the SHELL and the screens alike, and the shell is
 * mounted by five different routes. The param is on the `/portal` layout route's schema
 * (`search.ts`), so it is in the full search of every one of them.
 *
 * It is the RAW param that travels between screens, never the resolved contract: a link that
 * carried the resolved id would put a GUID in the URL of every tenant who has one contract and
 * never chose anything.
 */
export function usePortalContractParam(): string | undefined {
  return useSearch({ strict: false }).contract;
}

/**
 * The contract in view, or `undefined` when the caller has none at all.
 *
 * @param contracts the caller's own contracts as `GET /api/v1/portal/contracts` ordered them.
 * @param requested the `?contract=` of the URL, when there is one.
 */
export function contractInView(
  contracts: readonly PortalContractListItem[] | undefined,
  requested: string | undefined,
): PortalContractListItem | undefined {
  if (contracts === undefined || contracts.length === 0) return undefined;

  const named = contracts.find((contract) => contract.id === requested);
  if (named !== undefined) return named;

  return contracts.find((contract) => contract.status === 'ACTIVE') ?? contracts[0];
}

/**
 * Whether §6.6's condition for rendering the selector is met — „when the tenant has several".
 *
 * A separate function rather than `contracts.length > 1` at three call sites, because the
 * condition is the document's and the reading of it is one decision: an ended contract still
 * counts, because its rent history and its documents are still the tenant's and still need a
 * way to be reached.
 */
export function hasSeveralContracts(
  contracts: readonly PortalContractListItem[] | undefined,
): boolean {
  return (contracts?.length ?? 0) > 1;
}

/**
 * The one-line label a contract wears in the selector, on the home card and above a list.
 *
 * The address first, because that is how a tenant knows which of two flats this is; the unit
 * when the contract names one; the contract number never — it is the landlord's filing
 * reference and it is on the detail, not in a picker. A contract whose property carries no
 * address falls back to the property's name, which 03 §7.2 makes NOT NULL.
 */
export function contractLabel(contract: PortalContractListItem): string {
  const place = contract.propertyAddress ?? contract.propertyName;
  return contract.unitName == null ? place : `${place} · ${contract.unitName}`;
}
